import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Truck, BadgePercent, Boxes, Headset, Leaf, ShieldCheck,
  Coffee, ShoppingCart, Factory, HeartPulse, Utensils, SprayCan, Quote, HardHat, Baby,
} from "lucide-react";
import { getCategories, getIndustries, getBrands, getTestimonials } from "../lib/api";

const fade = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 } }),
};

const SectionHead = ({ eyebrow, title, sub, cta }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-12">
    <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}>
      <span className="text-xs tracking-[0.2em] uppercase text-vr-brand font-bold">{eyebrow}</span>
      <h2 className="mt-4 font-display font-extrabold tracking-tight leading-[1.02] text-3xl md:text-5xl text-vr-ink max-w-2xl">{title}</h2>
      {sub && <p className="mt-4 text-vr-dim max-w-xl">{sub}</p>}
    </motion.div>
    {cta && (
      <Link to={cta.to} className="group inline-flex items-center gap-2 text-vr-navy font-semibold hover:text-vr-brand transition-colors whitespace-nowrap">
        {cta.label} <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
      </Link>
    )}
  </div>
);

export const FeaturedCategories = () => {
  const [cats, setCats] = useState([]);
  useEffect(() => { getCategories().then(setCats).catch(() => {}); }, []);
  return (
    <section id="categories" data-testid="featured-categories" className="py-20 md:py-28 bg-vr-bg">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <SectionHead eyebrow="Shop by category" title="All Categories" sub="Everything a modern operation needs — organised into ten wholesale ranges." cta={{ to: "/products", label: "View all products" }} />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {cats.map((c, i) => (
            <motion.div key={c.slug} custom={i} variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-40px" }}>
              <Link to={`/category/${c.slug}`} data-testid={`category-card-${c.slug}`} className="group block rounded-2xl border border-vr-border bg-white overflow-hidden hover:border-vr-brand hover:shadow-xl transition-all duration-300">
                <div className="aspect-square bg-white overflow-hidden">
                  <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-4 border-t border-vr-border">
                  <h3 className="font-display font-bold text-vr-ink leading-tight group-hover:text-vr-brand transition-colors">{c.name}</h3>
                  <p className="mt-1 text-xs text-vr-dim line-clamp-1">{c.tagline}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WHY = [
  { icon: Truck, title: "Nationwide Delivery", body: "Fast, reliable dispatch to businesses right across New Zealand." },
  { icon: BadgePercent, title: "Wholesale Pricing", body: "Genuine trade pricing that sharpens as your volume grows." },
  { icon: Boxes, title: "Huge Range", body: "10 categories and thousands of lines — one supplier, one account." },
  { icon: Headset, title: "Expert Support", body: "Real people who know packaging and help you order right." },
  { icon: Leaf, title: "Sustainable Options", body: "Compostable, recyclable and recycled-content options across the range." },
  { icon: ShieldCheck, title: "Quality Products", body: "Trusted brands and quality product lines you can rely on daily." },
];

export const WhyChoose = () => (
  <section id="why" data-testid="why-choose" className="py-20 md:py-28 bg-vr-bg2">
    <div className="max-w-[1500px] mx-auto px-6 md:px-10">
      <SectionHead eyebrow="Why VR" title="Why businesses choose VR Packaging" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {WHY.map((w, i) => (
          <motion.div key={w.title} custom={i} variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }} className="rounded-2xl bg-white border border-vr-border p-7 hover:shadow-lg transition-shadow">
            <span className="grid place-items-center w-12 h-12 rounded-xl bg-vr-brand/10 text-vr-brand mb-5"><w.icon size={22} /></span>
            <h3 className="font-display font-bold text-lg text-vr-ink mb-2">{w.title}</h3>
            <p className="text-sm text-vr-dim leading-relaxed">{w.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const IND_ICON = { coffee: Coffee, "shopping-cart": ShoppingCart, factory: Factory, "hard-hat": HardHat, "heart-pulse": HeartPulse, utensils: Utensils, "spray-can": SprayCan, baby: Baby };

export const Industries = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { getIndustries().then(setItems).catch(() => {}); }, []);
  return (
    <section id="industries" data-testid="industries-section" className="py-20 md:py-28 bg-vr-bg">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <SectionHead eyebrow="Who we supply" title="Industries we serve" sub="Trusted by operations of every size, throughout New Zealand." cta={{ to: "/industries", label: "See all industries" }} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((ind, i) => {
            const Icon = IND_ICON[ind.icon] || Boxes;
            return (
              <motion.div key={ind.slug} custom={i} variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <Link
                  to={`/industries/${ind.slug}`}
                  data-testid={`home-industry-${ind.slug}`}
                  className="group block rounded-2xl border border-vr-border bg-white p-7 hover:border-vr-brand hover:shadow-lg transition-all"
                >
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-vr-navy text-white mb-5 group-hover:bg-vr-brand transition-colors"><Icon size={22} /></span>
                  <h3 className="font-display font-bold text-lg text-vr-ink mb-2 group-hover:text-vr-brand transition-colors">{ind.name}</h3>
                  <p className="text-sm text-vr-dim leading-relaxed">{ind.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-vr-navy group-hover:text-vr-brand transition-colors">
                    Explore range <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Brands = () => {
  const [brands, setBrands] = useState([]);
  useEffect(() => { getBrands().then(setBrands).catch(() => {}); }, []);
  const row = [...brands, ...brands];
  return (
    <section id="brands" data-testid="brands-section" className="py-16 bg-vr-navy overflow-hidden">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 mb-8">
        <p className="text-center text-xs uppercase tracking-[0.2em] text-white/50 font-semibold">Trusted brands we stock</p>
      </div>
      <div className="flex whitespace-nowrap animate-marquee">
        {row.map((b, i) => (
          <span key={i} className="mx-8 font-display font-bold text-2xl md:text-3xl text-white/40 hover:text-white transition-colors">{b}</span>
        ))}
      </div>
    </section>
  );
};

export const Testimonials = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { getTestimonials().then(setItems).catch(() => {}); }, []);
  return (
    <section data-testid="testimonials-section" className="py-20 md:py-28 bg-vr-bg2">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <SectionHead eyebrow="Customer stories" title="What our customers say" />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((t, i) => (
            <motion.div key={t.name} custom={i} variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }} className="rounded-2xl bg-white border border-vr-border p-7 flex flex-col">
              <Quote size={28} className="text-vr-brand/30 mb-4" />
              <p className="text-vr-ink leading-relaxed flex-1">"{t.quote}"</p>
              <div className="mt-6 pt-5 border-t border-vr-border">
                <div className="font-display font-bold text-vr-ink">{t.name}</div>
                <div className="text-sm text-vr-dim">{t.role}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const TradeAccountCTA = () => (
  <section data-testid="trade-cta-section" className="py-20 md:py-28 bg-vr-bg">
    <div className="max-w-[1500px] mx-auto px-6 md:px-10">
      <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-vr-navy via-[#12405b] to-vr-brand p-10 md:p-16">
        <div className="absolute -top-16 -right-10 w-72 h-72 rounded-full bg-vr-brand/40 blur-3xl" />
        <div className="absolute -bottom-14 -left-12 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
        <div className="relative max-w-2xl">
          <h2 className="font-display font-extrabold tracking-tight text-white text-3xl md:text-5xl leading-[1.05]">Open a trade account and unlock wholesale pricing.</h2>
          <p className="mt-5 text-white/80 text-lg">Apply in minutes. Get dedicated account support, volume pricing and consolidated ordering across every category.</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/trade-account" data-testid="trade-cta-apply" className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-vr-navy font-semibold hover:bg-vr-hygiene transition-colors">
              Open a Trade Account <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/contact" className="inline-flex items-center px-8 py-4 rounded-full border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors">
              Request a Quote
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export { SectionHead };
